/**
 * The compliance adapter seam (SE-7002, FC-1.1).
 *
 * This module is the ONLY place the Live Impact surface acquires data. Today
 * it evaluates fixtures locally; when the FC-0.1 rule-evaluation service and
 * the FC schema land, only this file changes and no component learns where the
 * verdict came from. Nothing here may import generated GraphQL types, because
 * the FC schema does not exist and running codegen against the deployed
 * endpoint would not produce it.
 *
 * The scenario-cumulative requirement lives here too, but only for the
 * compliance verdict (state/alertCount/headlines) and the Net book value
 * row — those describe the whole scenario AFTER the action being edited, so
 * the caller hands in every already-staged action alongside the in-flight
 * terms, and the action being edited replaces its own staged copy rather
 * than double-counting. The Wage/yr and Amortisation/yr rows are the
 * OPPOSITE on purpose (SE-8994): each reports THIS action's own
 * contribution alone, never folded together with `others` — a scenario
 * with a prior saved action must not add that action's wage/amortisation
 * onto the one currently being typed.
 * @module features/financial-compliance/adapters/scenarioImpactSource
 */

import { useMemo } from "react";

import {
  contributionFor,
  countUnrecordedLoanShares,
  EMPTY_PLAYER_BASELINE,
  hasSufficientTerms,
  sumContributions,
} from "../domain/actionFinancials";
import { evaluateRules } from "../domain/evaluateRules";
import { formatMillions, formatSignedMillions } from "../domain/formatImpact";
import { formatUnrecordedCoveredShareNote } from "../domain/unrecordedCoveredShare";
import {
  countAlerts,
  deriveVerdictState,
  headlinesByLayer,
} from "../domain/verdict";
import { EMPTY_CLUB_BASELINE, EMPTY_RULE_CONFIG } from "./complianceFixtures";

import type {
  ActionTerms,
  PlayerBaselineFinancials,
} from "../domain/actionFinancials";
import type {
  ClubFinancialBaseline,
  ComplianceRuleConfig,
} from "../domain/evaluateRules";
import type {
  ComplianceRuleEvaluation,
  ImpactDelta,
  LiveImpactModel,
} from "../types";

/** One action already staged in the scenario. */
export interface StagedAction {
  /** The player the action applies to. */
  readonly playerId: string;
  /** The action's recorded terms. */
  readonly terms: ActionTerms;
}

/**
 * A verdict the SERVER reached, against the club's own rules and figures.
 *
 * SE-8458. When this is present it supersedes the client-side rule evaluation
 * outright — not merges with it. The client engine models three rules over an
 * invented regime; the server models the club's actual registry. Blending the
 * two would report a verdict that is neither.
 *
 * Built by `adapters/draftEvaluationSource.ts`, which owns the query. Kept
 * structural, in the panel's OWN vocabulary, so this module still imports no
 * generated GraphQL type — see the header.
 */
export interface ServerScenarioEvaluation {
  /** Every rule the server evaluated, already in the panel's vocabulary. */
  readonly rules: readonly ComplianceRuleEvaluation[];
  /**
   * The club's net book value BEFORE this scenario — the roster's per-player
   * book values summed (SE-8794) — or null when the viewer's tier may not see
   * it or no player carries one. Null is not zero: a payload without book
   * values is not a club that owns nothing.
   */
  readonly clubNetBookValue: number | null;
  /** True while a newer evaluation is in flight. */
  readonly isPending: boolean;
}

/** Everything the adapter needs to produce a verdict. */
export interface ScenarioImpactInput {
  /** The player whose action is being edited, when known. */
  readonly playerId?: string | null;
  /** The in-flight terms from the open modal. */
  readonly terms: ActionTerms;
  /** Every action already staged in the scenario, including this player's. */
  readonly stagedActions?: readonly StagedAction[];
  /**
   * Whether this club's tier includes the compliance surface at all.
   *
   * `false` renders NOTHING — not a placeholder and not a call to action,
   * either of which would disclose that a higher-tier field exists.
   */
  readonly isPermitted?: boolean;
  /** Per-player baselines, for the Sell and Loan relief models. */
  readonly playerBaselines?: Readonly<Record<string, PlayerBaselineFinancials>>;
  /** Override the club's configured regime (gallery and tests). */
  readonly ruleConfig?: ComplianceRuleConfig;
  /** Override the club's pre-scenario position (gallery and tests). */
  readonly clubBaseline?: ClubFinancialBaseline;
  /**
   * The club's REAL verdict, as the server evaluated it (SE-8458).
   *
   * THREE states, and the difference between the last two is load-bearing:
   *
   * - **omitted / `undefined`** — this caller drives the LOCAL engine against
   *   `ruleConfig`/`clubBaseline`. The gallery and the unit tests.
   * - **`null`** — a server evaluation was asked for and has NOT arrived yet
   *   (or the read failed). There is no verdict to show.
   * - **an evaluation** — the server's answer, which supersedes the local
   *   engine outright.
   *
   * These used to collapse: the field defaulted to `null`, so "nobody asked
   * for a server verdict" and "the server has not answered" were the same
   * value, and both fell through to the local engine over EMPTY_RULE_CONFIG.
   * The panel therefore rendered a SETTLED "NOT CONFIGURED" in the window
   * before the first response — and kept it forever if that request failed.
   * That is a confident false claim about the club, which is the whole defect
   * SE-8458 exists to remove, reintroduced one layer down.
   */
  readonly serverEvaluation?: ServerScenarioEvaluation | null;
}

/** What the adapter returns — the shape a real data hook will keep. */
export interface ScenarioImpactResult {
  /** What the panel should render. */
  readonly data: LiveImpactModel;
  /** True while the underlying source is in flight. */
  readonly loading: boolean;
  /** Non-null when the underlying source failed. */
  readonly error: Error | null;
}

/**
 * What an absolute row says when the club's own figure is unknown: an em dash.
 *
 * It used to read "Not configured" (SE-8794). The only absolute row is Net book
 * value, and that figure is the SUM of the roster's per-player book values —
 * nothing a club enters — so it is unknown only because the viewer's tier
 * withholds it or no player carries one. "Not configured" sent users looking
 * for a setting that does not exist. The dash is the one the squad-planning
 * tables already print for a missing figure; it is restated here rather than
 * imported because this feature may not depend on squad-planning.
 */
const UNKNOWN_POSITION = "\u2014";

/**
 * Row labels for the Live Impact deltas.
 *
 * Wage and amortisation are taken verbatim from the Live Impact frames: both
 * are this action's own signed delta, same as the figure beside them. Net
 * book value is not a delta — it stays the whole scenario's unsigned
 * position (SE-8794) — and SE-8994 AC2 requires the row to say so rather than
 * reusing the bare label the two deltas above it use, which read as though it
 * were a third delta.
 */
const DELTA_LABELS = {
  wage: "Wage / yr",
  amortisation: "Amortisation / yr",
  netBookValue: "Net book value (scenario total)",
} as const;

/**
 * Read the live impact of a scenario action.
 *
 * The one behaviour callers must not lose: this NEVER returns anything that
 * gates Confirm. The verdict is informational, and a breach is a thing the
 * user is told, not a thing that stops them.
 * @param input - The action terms, staged scenario and optional overrides.
 * @returns The panel model, plus loading and error in the shape a real
 * data hook will keep.
 */
export const useScenarioImpact = (
  input: ScenarioImpactInput
): ScenarioImpactResult => {
  const {
    playerId,
    terms,
    stagedActions,
    isPermitted = true,
    playerBaselines,
    ruleConfig = EMPTY_RULE_CONFIG,
    clubBaseline = EMPTY_CLUB_BASELINE,
    // NO default. `undefined` and `null` mean different things here — see
    // ScenarioImpactInput.serverEvaluation.
    serverEvaluation,
  } = input;

  const data = useMemo<LiveImpactModel>(() => {
    if (!isPermitted) return { kind: "hidden" };
    if (!hasSufficientTerms(terms)) return { kind: "awaiting-input" };

    // Asked the server, no answer yet. Show the empty state the panel already
    // has rather than a verdict computed from a regime nobody configured —
    // and rather than inventing a new state, which SE-8458's UI decision
    // explicitly forbids ("before any verdict has been computed, the panel
    // keeps whatever empty/awaiting state it already has today").
    //
    // This also covers a FAILED read, where no evaluation ever arrives. Saying
    // "nothing yet" indefinitely is honest; saying "NOT CONFIGURED" would be a
    // claim about the club that the client is in no position to make.
    if (serverEvaluation === null) return { kind: "awaiting-input" };

    const baselineFor = (id: string): PlayerBaselineFinancials =>
      playerBaselines?.[id] ?? EMPTY_PLAYER_BASELINE;

    // The action being edited supersedes its own staged copy — counting both
    // would double the player and report an impact the scenario never has.
    const others = (stagedActions ?? []).filter(
      staged => staged.playerId !== playerId
    );
    // THIS action's own contribution, isolated from every other staged
    // action. The Wage/yr and Amortisation/yr rows render this, never
    // `total` below — SE-8994 found the panel reporting the whole
    // scenario's cumulative change once a prior action was already saved,
    // because those rows read `total` (which folds in every `others`
    // entry) rather than just the terms on screen.
    const ownContribution = contributionFor(terms, baselineFor(playerId ?? ""));
    // The scenario-cumulative total: every other staged action plus this
    // one. Still feeds the rule evaluation and the Net book value row below
    // — both legitimately describe the whole scenario, which is the one
    // place "cumulative" remains correct.
    const total = sumContributions([
      ...others.map(staged =>
        contributionFor(staged.terms, baselineFor(staged.playerId))
      ),
      ownContribution,
    ]);

    // Counted over EXACTLY the actions that were just summed — same set, same
    // exclusion of the edited player's staged copy. A loan persisted before
    // the covered-share field existed contributes no wage relief above
    // (SE-7411), and this is the sentence that says so. A figure that is
    // quietly short is the failure the covered-share field was added to
    // prevent, so the disclosure ships with the figure or not at all.
    const unrecordedShareNote = formatUnrecordedCoveredShareNote(
      countUnrecordedLoanShares([...others.map(staged => staged.terms), terms])
    );

    const aggregate = {
      wagePerYear: clubBaseline.wagePerYear + total.wagePerYear,
      amortisationPerYear:
        clubBaseline.amortisationPerYear + total.amortisationPerYear,
      netBookValue: clubBaseline.netBookValue + total.netBookValue,
      squadSize: clubBaseline.squadSize + total.squadSize,
      revenuePerYear: clubBaseline.revenuePerYear,
    };

    // The server's evaluation supersedes the local engine when it is here.
    // `evaluateRules` is on the right of `??` deliberately: it must not run at
    // all on the live path, where its fixture regime is exactly the thing
    // SE-8458 removed.
    const rules =
      serverEvaluation?.rules ?? evaluateRules(ruleConfig, aggregate);

    // The club's own book value, which the NBV row reports a POSITION against
    // rather than a delta. From the server on the live path — where it can be
    // genuinely unknown — and from the caller's baseline otherwise, which keeps
    // the gallery and the unit tests byte-identical to before.
    const clubNetBookValue =
      serverEvaluation == null
        ? clubBaseline.netBookValue
        : serverEvaluation.clubNetBookValue;

    const deltas: readonly ImpactDelta[] = [
      {
        id: "wage",
        label: DELTA_LABELS.wage,
        value: formatSignedMillions(ownContribution.wagePerYear),
      },
      {
        id: "amortisation",
        label: DELTA_LABELS.amortisation,
        value: formatSignedMillions(ownContribution.amortisationPerYear),
      },
      {
        id: "net-book-value",
        label: DELTA_LABELS.netBookValue,
        // Without the club's own figure there is no position to state. Showing
        // this deal's contribution alone under an absolute label would read as
        // the club's whole book — a number the client has not been told.
        value:
          clubNetBookValue == null
            ? UNKNOWN_POSITION
            : formatMillions(clubNetBookValue + total.netBookValue),
      },
    ];

    return {
      kind: "verdict",
      verdict: {
        state: deriveVerdictState(rules),
        alertCount: countAlerts(rules),
        deltas,
        headlines: headlinesByLayer(rules),
        unrecordedShareNote,
        isPending: serverEvaluation?.isPending ?? false,
      },
    };
  }, [
    clubBaseline,
    isPermitted,
    playerBaselines,
    playerId,
    ruleConfig,
    serverEvaluation,
    stagedActions,
    terms,
  ]);

  return { data, loading: false, error: null };
};
